import type { PageMode } from '@coorum-research/shared-client-user/components/CoorumResearch'
import type { AnswerDetailResponse } from '@coorum-research/shared-common/api-response'
import type { CurrentUserDataPayload } from '@coorum-research/shared-common/types/research-setting.type'
import { answerApi } from '@coorum-research/shared-client-user/apis/answers.api'
import { ResearchSdkFactory } from '@coorum-research/shared-client-user/sdk/research-sdk-factory'
import { isLikelyBot } from '@coorum-research/shared-client-user/utils/blocked-uas.util'
import { renderResearchLauncher } from '@coorum-research/shared-client-user/utils/RenderResearchLauncher'
import { renderResearchPage } from '@coorum-research/shared-client-user/utils/RenderResearchPage'
import './launcher.css'

const launcherRootEl = window.parent.document.querySelector<HTMLElement>('#cr-research-launcher')!

// create style element and add data container.css
const styleEl = document.createElement('style')
launcherRootEl.insertAdjacentElement('afterend', styleEl)
styleEl.innerHTML = `
.cr-research-launcher p, .cr-research-container p {
    margin: 0;
    padding: 0;
}
.cr-research-launcher {
    font-size: 16px;
    width: 100%;
    position: fixed;
    z-index: 2147483647 !important;
}
.cr-research-container {
    font-size: 16px;
    position: fixed !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    background-color: #fff !important;
    z-index: 2147483647 !important;
}

.cr-research-container::-webkit-scrollbar {
  display: none;
}

.cr-research-container[data-display-mode="popup"] {
    width: 390px !important;
    min-height: 200px !important;
    max-height: 780px !important;
    height: 100%;
    box-shadow: rgba(255, 255, 255, 0.12) 0px 0px 2px 0px inset, rgba(0, 0, 0, 0.05) 0px 0px 2px 1px, rgba(0, 0, 0, 0.3) 0px 12px 60px;
    border-radius: 12px !important;
    visibility: hidden !important;
    transition: transform, opacity 400ms cubic-bezier(0.36, 0, 0, 1) 0s;
    will-change: transform, opacity !important;
    opacity: 0 !important;
}

.cr-research-container-open[data-display-mode="popup"] {
    visibility: visible !important;
    opacity: 1 !important;
}

@keyframes slideUp {
  0% {
    transform: translateY(100%);
    opacity: 0.8;
    visibility: hidden;
  }
  100% {
    transform: translateY(32px);
    opacity: 1;
    visibility: visible;
  }
}

@keyframes slideDown {
  0% {
    transform: translateY(32px);
    opacity: 1;
    visibility: visible;
  }
  95% {
    opacity: 1;
  }
  100% {
    transform: translateY(100%);
    opacity: 0;
    visibility: hidden;
  }
}

.cr-research-container[data-display-mode="sheet"] {
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    visibility: hidden;
}

.cr-research-container-open[data-display-mode="sheet"] {
    animation: slideUp 400ms cubic-bezier(0.36, 0, 0, 1) forwards;
}

.cr-research-container-open[data-display-mode="sheet"]:before {
    position: fixed;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background: rgba(0, 0, 0, 30%);
    display: block;
    content: '';
    width: 100%;
    height: 100%;
    z-index: -1;
}

.cr-research-container-close[data-display-mode="sheet"] {
    animation: slideDown 400ms cubic-bezier(0.36, 0, 0, 1) forwards;
}

.cr-research-scroll-lock {
  overflow-x: hidden !important;
  overflow-y: hidden !important;
}
`

function coorumResearchOnBoot(): void {
  if (isLikelyBot(window.navigator, [])) {
    return
  }
  void renderResearchLauncher()
}

/**
 * Launcherはなしで直で、<CoorumResearch>をrenderする
 * javascript APIでの表示は、popupのconfigJsonを使えないので固定位置に表示する（仕様検討・修正予定）
 */
function coorumResearchOnShowPage(initPage: PageMode): void {
  if (isLikelyBot(window.navigator, [])) {
    return
  }

  renderResearchPage({
    show: true,
    initPage,
  })
}

async function coorumResearchOnCurrentUserData(payload: CurrentUserDataPayload): Promise<AnswerDetailResponse | null> {
  if (isLikelyBot(window.navigator, [])) {
    return null
  }

  const { getAnswerDetail } = answerApi()

  switch (payload.dataType) {
  case 'answer':
    return await getAnswerDetail(payload.params.answerId)
  default:
    console.error(`Unknown dataType: ${payload.dataType}`)
    return null
  }
}

const sdkFactory = new ResearchSdkFactory(window.parent.CoorumResearch.q)
window.parent.CoorumResearch = sdkFactory.getSdk()
window.parent.CoorumResearch('init', coorumResearchOnBoot, coorumResearchOnShowPage, coorumResearchOnCurrentUserData)
